import { useQuery } from '@tanstack/react-query'
import { useCan } from '@if/mui'
import { access } from '@src/access'
import { getApiClient } from '../api-client'
import { useMemo } from 'react'

export interface UnreadEntry {
  creditApplicationId: string
  financingProgramId: string
}

export interface SignalRConnectionInfo {
  url: string
  accessToken: string
}

const SCOPE = 'chat-message'

// Query Keys Factory
export const chatMessageKeysFactory = {
  all: [SCOPE] as const,
  unreadEntries: () => [SCOPE, 'unread-entries'] as const,
}

// GET unread entries (list of unread creditApplicationId + financingProgramId)
const getUnreadEntries = async (): Promise<UnreadEntry[]> => {
  const response = await getApiClient().get<UnreadEntry[]>('/ChatMessages/Unread/entries')
  return response.data
}

export function useUnreadEntries(): [UnreadEntry[], boolean] {
  const isEnabled = useCan(access.messaging)
  const { data, isFetching } = useQuery({
    queryKey: chatMessageKeysFactory.unreadEntries(),
    queryFn: () => getUnreadEntries(),
    enabled: isEnabled,
  })
  return [data ?? [], isFetching]
}

/**
 * Derives a Set of unread credit application IDs from the unread entries cache.
 */
export function useUnreadCreditApplicationIds(): Set<string> {
  const [entries] = useUnreadEntries()
  return useMemo(() => new Set(entries.map((e) => e.creditApplicationId)), [entries])
}

// SignalR negotiate
export const negotiate = async (): Promise<SignalRConnectionInfo> => {
  const response = await getApiClient().post<SignalRConnectionInfo>('/ChatTemplateMessages/negotiate')
  return response.data
}

// SignalR join group
export const joinAllMessagesGroup = async (
  connectionId: string,
  merchantId: string,
  isAgent: boolean,
): Promise<void> => {
  await getApiClient().post('/ChatTemplateMessages/management/joinAllMessagesGroup', {
    connectionId,
    merchantId,
    isAgent,
  })
}
